import { useInView } from 'react-intersection-observer';
import { useAnimation } from 'framer-motion';
import { useEffect } from 'react';

// Optimized animation variants for consistent performance
export const animationVariants = {
    // Fade animations - faster and smoother
    fadeIn: {
        hidden: { opacity: 0 },
        visible: { 
            opacity: 1,
            transition: { 
                duration: 0.3, 
                ease: [0.25, 0.46, 0.45, 0.94] 
            }
        }
    },
    fadeInUp: {
        hidden: { opacity: 0, y: 20 },
        visible: { 
            opacity: 1, 
            y: 0,
            transition: { 
                duration: 0.35, 
                ease: [0.25, 0.46, 0.45, 0.94] 
            }
        }
    },
    fadeInDown: {
        hidden: { opacity: 0, y: -20 },
        visible: { 
            opacity: 1, 
            y: 0,
            transition: { 
                duration: 0.35, 
                ease: [0.25, 0.46, 0.45, 0.94] 
            }
        }
    },
    fadeInLeft: {
        hidden: { opacity: 0, x: -20 },
        visible: { 
            opacity: 1, 
            x: 0,
            transition: { 
                duration: 0.35, 
                ease: [0.25, 0.46, 0.45, 0.94] 
            }
        }
    },
    fadeInRight: {
        hidden: { opacity: 0, x: 20 },
        visible: { 
            opacity: 1, 
            x: 0,
            transition: { 
                duration: 0.35, 
                ease: [0.25, 0.46, 0.45, 0.94] 
            }
        }
    },
    
    // Scale animations - snappier
    scaleIn: {
        hidden: { opacity: 0, scale: 0.95 },
        visible: { 
            opacity: 1, 
            scale: 1,
            transition: { 
                duration: 0.25, 
                ease: [0.25, 0.46, 0.45, 0.94] 
            }
        }
    },
    
    // Slide animations - reduced distance for smoother feel
    slideInUp: {
        hidden: { y: 30, opacity: 0 },
        visible: { 
            y: 0, 
            opacity: 1,
            transition: { 
                duration: 0.3, 
                ease: [0.25, 0.46, 0.45, 0.94] 
            }
        }
    },
    
    // Stagger animations for lists - faster stagger
    staggerContainer: {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.05,
                delayChildren: 0.1
            }
        }
    },
    
    // Hover animations - snappier
    hover: {
        scale: 1.03,
        transition: { 
            duration: 0.15, 
            ease: [0.25, 0.46, 0.45, 0.94] 
        }
    },
    
    tap: {
        scale: 0.97,
        transition: { 
            duration: 0.08, 
            ease: [0.25, 0.46, 0.45, 0.94] 
        }
    }
};

// Custom hook for optimized scroll-triggered animations
export const useOptimizedAnimation = (threshold = 0.1, triggerOnce = true) => {
    const controls = useAnimation();
    const [ref, inView] = useInView({ 
        threshold, 
        triggerOnce,
        rootMargin: '0px 0px -50px 0px' // Start animation slightly before element comes into view
    });

    useEffect(() => {
        if (inView) {
            controls.start('visible');
        }
    }, [inView, controls]);

    return { ref, controls, inView };
};

// Optimized transition configurations - faster and smoother
export const transitions = {
    fast: { duration: 0.15, ease: [0.25, 0.46, 0.45, 0.94] },
    medium: { duration: 0.25, ease: [0.25, 0.46, 0.45, 0.94] },
    slow: { duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] },
    bounce: { duration: 0.4, ease: [0.68, -0.55, 0.265, 1.55] },
    elastic: { duration: 0.5, ease: [0.175, 0.885, 0.32, 1.275] }
};

// Utility function for staggered animations - faster
export const createStaggeredAnimation = (delay = 0.05) => ({
    hidden: { opacity: 0, y: 15 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.3,
            ease: [0.25, 0.46, 0.45, 0.94],
            delay
        }
    }
});

// Performance-optimized motion props
export const motionProps = {
    // Enable hardware acceleration
    style: {
        willChange: 'transform, opacity',
        transform: 'translateZ(0)',
        backfaceVisibility: 'hidden',
        perspective: '1000px'
    }
};

// Enhanced performance optimization hook
export const useEnhancedAnimation = () => {
    useEffect(() => {
        // Preload animations for better performance
        const preloadAnimations = () => {
            requestAnimationFrame(() => {
                const animatedElements = document.querySelectorAll('.gpu-accelerated, [data-motion]');
                animatedElements.forEach((el) => {
                    // Force GPU layer creation
                    el.style.transform = 'translateZ(0)';
                    el.style.willChange = 'transform, opacity';
                });
            });
        };

        // Run preload after initial render
        const timer = setTimeout(preloadAnimations, 50);
        return () => clearTimeout(timer);
    }, []);
};
